import React, { useState } from 'react';
import cn from 'classnames';

import { useRouter } from 'next/router';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { loginUser } from '../../state/actions/loginAction';
import useRedirectURL from '../../hooks/useRedirectURL';

import styles from './styles/UsernameOption.module.scss';
import GoogleLogin from './GoogleLogin';
import { storeAndRedirectUser } from './storeAndRedirectUser';

const UsernameOption = ({ loginState, dispatchLoginUser }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [loginValues, setLoginValues] = useState({
    username: '',
    password: '',
  });

  const businessListState = useSelector(store => store.businessListState);

  const handleChange = prop => event => {
    if (loginState.isError) {
      dispatchLoginUser({ type: 'DEFAULT' });
    }
    setLoginValues({ ...loginValues, [prop]: event.target.value });
  };

  const handleSignin = e => {
    e.preventDefault();
    const username = `${loginValues.username}`.replace(/[ +]/g, '');
    const payload = {
      username: username,
      password: loginValues.password,
    };
    if (loginValues.username !== '' && loginValues.password !== '') {
      dispatchLoginUser({
        type: 'LOADING',
      });
      loginUser(payload)
        .then(response => {
          const redirect = router.query.redirect;
          storeAndRedirectUser({
            authResponse: response,
            router,
            redirect,
            dispatch,
          });
        })
        .catch(error => {
          let knownErrorStatusCodesResponses = {
            400: 'Username or password is incorrect',
            404: 'Username or password is incorrect',
            401: 'Username or password is incorrect',
          };

          dispatchLoginUser({
            type: 'ERROR',
            errorMessage:
              knownErrorStatusCodesResponses[error.response.status] ||
              error.response.data.error_message,
          });

          return error.response;
        });
    } else {
      dispatchLoginUser({
        type: 'ERROR',
        errorMessage: 'Fill both the username and the password fields',
      });
    }
  };

  const { registerLink } = useRedirectURL();

  return (
    <div className={styles.usernamePasswordOption}>
      <div className={styles.wrapper}>
        <div className={styles.messageHeader}>
          <div className={cn(styles.title, 'theme-text')}>SIGN IN</div>
          <span className={styles.welcomeInfo}>
            Don&apos;t have an ICTLIFE account?{' '}
            <Link href={registerLink}>Register here</Link>
          </span>
        </div>

        <GoogleLogin text="Sign In With Google" dispatch={dispatchLoginUser} />

        <div className="text-center mb-4 font-semibold">OR</div>
        <form
          className={cn('app-form', styles.usernamePasswordHolder)}
          onSubmit={handleSignin}
        >
          {router.query?.username && (
            <div className={styles.loginSuccessMessage}>
              {`Password set successfully. Use it to login together with your username: ${router.query.username}`}
            </div>
          )}
          <div className="input-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              data-testid="username-input"
              onChange={handleChange('username')}
              value={loginValues.username}
              autoComplete="username"
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              data-testid="password-input"
              value={loginValues.password}
              onChange={handleChange('password')}
              autoComplete="current-password"
              required
            />
          </div>
          {loginState.isError && (
            <div className="error-section" data-testid="login-error-section">
              {loginState.errorMessage}
            </div>
          )}
          <div className={styles.passwordActions}>
            <div>
              I forgot my password.{' '}
              <Link href="/user/forgotPassword">
                <a className={cn(styles.action, 'theme-text')}>Reset here</a>
              </Link>
            </div>
          </div>
          <div className={cn(styles.loginButton, 'button')}>
            <button
              className="custom-button"
              data-testid="submit-button"
              disabled={loginState.isLoading || businessListState.isLoading}
            >
              Login
            </button>
            {(loginState.isLoading || businessListState.isLoading) && (
              <CircularProgress size={25} className="button-loader" />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
export default UsernameOption;
